/**
 * /check-in/session/{id}/print — printer-friendly Check-In Slip.
 *
 * Designed to print cleanly from any browser via the system print dialog
 * (Ctrl/Cmd + P, or share-sheet → Print on mobile). Avoids relying on a
 * thermal-printer SDK so it works on whatever the shop has.
 *
 * Layout:
 *   ┌───────────────────────────────────────────────┐
 *   │  TechSolvers · CHECK-IN SLIP                  │
 *   │                                               │
 *   │  Session # 2026051301                         │
 *   │  Drop-off  13 May 2026, 2:35 PM               │
 *   │  Received by Steve Thomas                     │
 *   │  ─────────────────────────────────            │
 *   │  Customer                                     │
 *   │   Acme Co Ltd                                 │
 *   │   Jane Doe · 0412 345 678                     │
 *   │  ─────────────────────────────────            │
 *   │  Devices (2)                                  │
 *   │   1. Laptop — Setup Win11 + OneDrive          │
 *   │   2. Desktop — Profile migration              │
 *   │  ─────────────────────────────────            │
 *   │  TechSolvers · 03 9123 4567                   │
 *   │  Scan: http://…/check-in/session/{id}         │
 *   └───────────────────────────────────────────────┘
 */

import React, { useEffect } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { useCheckInSession } from '../hooks/useCheckInSession';

export interface CheckInPrintSlipPageProps {
  sessionId: string;
  onBack: () => void;
  /** Optional: auto-fire the browser print dialog as soon as data loads. */
  autoPrint?: boolean;
}

const DEVICE_LABEL: Record<string, string> = {
  laptop: 'Laptop',
  desktop: 'Desktop',
  all_in_one: 'All-in-One',
  phone: 'Phone',
  tablet: 'Tablet',
  hard_drive: 'Hard Drive',
  usb_flash_drive: 'USB Flash Drive',
};

export const CheckInPrintSlipPage: React.FC<CheckInPrintSlipPageProps> = ({
  sessionId,
  onBack,
  autoPrint = false,
}) => {
  const { session, isLoading, error } = useCheckInSession(sessionId);

  useEffect(() => {
    if (!autoPrint || !session) return;
    // Defer one tick so the layout settles before the print dialog opens.
    const t = window.setTimeout(() => window.print(), 200);
    return () => window.clearTimeout(t);
  }, [autoPrint, session]);

  if (isLoading && !session) return <Box sx={{ p: 4 }}>Loading…</Box>;
  if (error && !session) return <Box sx={{ p: 4, color: 'error.main' }}>{error}</Box>;
  if (!session) return null;

  const droppedOff = new Date(session.createdAt);
  const sessionUrl = `${window.location.origin}/check-in/session/${session.id}`;
  const businessLine = session.businessName && session.businessName !== session.customerName
    ? session.businessName
    : null;

  return (
    <>
      <PrintStyles />

      {/* On-screen toolbar — hidden during print */}
      <Box className="print-toolbar" sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack}>Back</Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}>
          Print
        </Button>
      </Box>

      {/* The slip itself */}
      <Box
        className="print-slip"
        sx={{
          maxWidth: 720,
          mx: 'auto',
          p: 4,
          fontFamily: '"Helvetica Neue", Arial, sans-serif',
          color: '#111',
        }}
      >
        <Stack spacing={1}>
          <Typography variant="overline" sx={{ letterSpacing: '0.15em', color: 'text.secondary' }}>
            TechSolvers · Check-In Slip
          </Typography>

          <Typography sx={{ fontSize: 56, fontWeight: 800, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
            {session.sessionNumber}
          </Typography>

          <Stack direction="row" spacing={3} sx={{ pt: 0.5 }}>
            <KV label="Drop-off" value={droppedOff.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })} />
            <KV label="Received by" value={session.createdBy || '—'} />
            {session.intakeType && <KV label="Type" value={labelForIntake(session.intakeType)} />}
          </Stack>
        </Stack>

        <Divider />

        <Section title="Customer">
          {businessLine && (
            <Typography sx={{ fontSize: 22, fontWeight: 700 }}>{businessLine}</Typography>
          )}
          <Typography sx={{ fontSize: 18 }}>
            {session.customerName || '—'}
          </Typography>
          <Typography sx={{ color: 'text.secondary' }}>
            {[session.customerPhone, session.customerEmail].filter(Boolean).join(' · ')}
          </Typography>
        </Section>

        <Divider />

        <Section title={`Devices (${session.draftJobs.length})`}>
          <Stack spacing={1}>
            {session.draftJobs.map((job, idx) => (
              <Box key={job.id}>
                <Typography sx={{ fontSize: 16 }}>
                  <strong>{idx + 1}. {DEVICE_LABEL[job.deviceType] ?? job.deviceType}</strong>
                  {job.serviceSummary ? ` — ${job.serviceSummary}` : ''}
                </Typography>
                {job.requirements && (
                  <Typography sx={{ color: 'text.secondary', ml: 3, fontSize: 14 }}>
                    {job.requirements}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </Section>

        <Divider />

        <Section title="">
          <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
            TechSolvers · (03) 9123 4567
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 11, mt: 0.5, wordBreak: 'break-all' }}>
            Scan: {sessionUrl}
          </Typography>
        </Section>
      </Box>
    </>
  );
};

const KV: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box>
    <Typography sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'text.secondary' }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{value}</Typography>
  </Box>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{ py: 2 }}>
    {title && (
      <Typography variant="overline" sx={{ letterSpacing: '0.15em', color: 'text.secondary', display: 'block', mb: 0.5 }}>
        {title}
      </Typography>
    )}
    {children}
  </Box>
);

const Divider: React.FC = () => (
  <Box sx={{ borderTop: '1px solid #ddd', my: 0 }} />
);

/**
 * Print-only styles. Hides the on-screen toolbar and resets margins so the
 * slip prints centred on standard paper without the staff app's chrome.
 */
const PrintStyles: React.FC = () => (
  <style
    // eslint-disable-next-line react/no-danger
    dangerouslySetInnerHTML={{
      __html: `
        @media print {
          body { margin: 0; }
          .print-toolbar { display: none !important; }
          .main-layout > header { display: none !important; }
          .print-slip { max-width: none !important; padding: 12mm !important; }
        }
      `,
    }}
  />
);

function labelForIntake(intakeType: string): string {
  if (intakeType === 'in_store') return 'In-Store';
  if (intakeType === 'online') return 'Online';
  if (intakeType === 'express') return 'Express';
  return intakeType;
}
